import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  borderPrimary,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
} from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';
import { TabbedSections } from '../../ui/TabbedSections';
import { LabeledFilterButton } from '../../ui/LabeledFilterButton';
import { useProductSearch } from '../../../hooks/useProductSearch';
import { productKey } from '../../../utils/reports/shared';
import { useWasteStore } from '../../../stores/appWasteStore';
import { WasteEntry } from '../../../services/waste/wasteService';
import { WasteProductOption } from './wasteLinkShared';
import { formatWasteQuantity, WASTE_REASON_LABELS } from '../../../utils/reports/wasteByProduct';

interface Props {
  /** The entry being linked. Null → the drawer is closed. */
  entry: WasteEntry | null;
  productOptions: WasteProductOption[];
  onClose: () => void;
}

const SUGGESTION_LIMIT = 5;
const BROWSE_LIMIT = 50;

/**
 * Single-entry link drawer, the waste twin of the inventory `ReLinkInventoryProductModal`:
 * slide-over, suggestions first, then a searchable browse of the whole order-history
 * product universe. Always `method: 'manual'`; a human choice outranks whatever the
 * automation decided.
 *
 * INGREDIENT entries only. A dish resolves to its cost-price analysis by name, so there is
 * nothing for a human to pick — a dish with no analysis gets the "Set up cost price" action
 * on the page instead.
 */
export default function LinkWasteEntryModal({ entry, productOptions, onClose }: Props) {
  const { t } = useTranslation();
  const updateLink = useWasteStore((s) => s.updateLink);

  const isOpen = entry != null;

  const [tab, setTab] = useState<'suggested' | 'browse'>('suggested');
  const [search, setSearch] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTab('suggested');
    setSearch('');
    setSelectedSuppliers([]);
    setSelectedCategories([]);
    // Open on the current link (when there is one), so a relink starts from what it says.
    setSelectedKey(
      entry?.linkedPlatform
        ? productKey(entry.linkedPlatform, entry.linkedArticleId ?? undefined, entry.linkedProductName ?? undefined)
        : null
    );
    setSaving(false);
    setError(null);
  }, [isOpen, entry]);

  // Filter options come from the full list, so a chosen supplier never hides the
  // categories you could still pick (same idiom as ReLinkInventoryProductModal).
  const supplierOptions = useMemo(
    () => Array.from(new Set(productOptions.map((p) => p.platform).filter(Boolean))).sort(),
    [productOptions]
  );

  const categoryOptions = useMemo(
    () => Array.from(new Set(productOptions.map((p) => p.category).filter((c): c is string => !!c))).sort(),
    [productOptions]
  );

  const productSource = useMemo(
    () =>
      productOptions.filter((p) => {
        if (selectedSuppliers.length > 0 && !selectedSuppliers.includes(p.platform)) return false;
        if (selectedCategories.length > 0 && (!p.category || !selectedCategories.includes(p.category))) return false;
        return true;
      }),
    [productOptions, selectedSuppliers, selectedCategories]
  );

  // Suggestions are the logged free-text name run against the universe; browsing runs
  // the user's own query (empty query = the full alphabetical list, capped).
  const productSuggestions = useProductSearch({
    data: productOptions,
    keys: ['productName', 'searchableId'],
    query: entry?.itemName ?? '',
    limit: SUGGESTION_LIMIT,
  });
  const productBrowse = useProductSearch({
    data: productSource,
    keys: ['productName', 'searchableId'],
    query: search,
    limit: BROWSE_LIMIT,
  });

  const handleSave = async () => {
    if (!entry || !selectedKey) return;
    setSaving(true);
    setError(null);
    try {
      const product = productOptions.find((p) => p.key === selectedKey);
      if (!product) return;
      await updateLink(entry.id, {
        kind: 'ingredient',
        platform: product.platform,
        // Omitted for no-articleId platforms: the column is meant to be NULL there,
        // and the identity keys off the name.
        articleId: product.articleId,
        productName: product.productName,
        method: 'manual',
      });
      onClose();
    } catch (err) {
      console.error('Failed to link waste entry', err);
      setError(t('waste.link.errorSave'));
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!entry) return;
    setSaving(true);
    setError(null);
    try {
      await updateLink(entry.id, { kind: null });
      onClose();
    } catch (err) {
      console.error('Failed to unlink waste entry', err);
      setError(t('waste.link.errorSave'));
      setSaving(false);
    }
  };

  const renderOption = (key: string, title: string, subtitle: string) => {
    const isSelected = selectedKey === key;
    return (
      <button
        type="button"
        key={key}
        onClick={() => setSelectedKey(isSelected ? null : key)}
        className={cn(
          'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left',
          isSelected
            ? 'bg-green-50 dark:bg-green-900/20 border-darkGreenColor dark:border-darkGreenColor-dark'
            : cn('bg-white dark:bg-zinc-900', borderPrimary, 'hover:bg-gray-50 dark:hover:bg-gray-800')
        )}
      >
        <span className="min-w-0">
          <span className={cn('block truncate', smallTextSize, semiBoldText, primaryText)}>{title}</span>
          {subtitle && <span className={cn('block truncate text-[11px]', tertiaryText)}>{subtitle}</span>}
        </span>
        <span
          className={cn(
            'shrink-0 w-4 h-4 rounded-full border flex items-center justify-center',
            isSelected ? 'border-darkGreenColor bg-darkGreenColor dark:border-darkGreenColor-dark dark:bg-darkGreenColor-dark' : 'border-gray-300 dark:border-zinc-600'
          )}
        >
          {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white dark:bg-white" />}
        </span>
      </button>
    );
  };

  const productSubtitle = (p: WasteProductOption): string => {
    const bits: string[] = [];
    if (p.platform) bits.push(p.platform);
    if (p.unitPrice) bits.push(`${currencyFormatter.format(p.unitPrice.value)} / ${p.unitPrice.unit}`);
    return bits.join(' · ');
  };

  const visibleProducts = tab === 'suggested' ? productSuggestions.results : productBrowse.results;
  const isEmpty = visibleProducts.length === 0;

  const footer = (
    <>
      <button type="button" onClick={onClose} disabled={saving} className={slideOverCancelClass}>
        {t('waste.link.cancel')}
      </button>
      {entry?.linkStatus === 'linked' && (
        <button type="button" onClick={handleUnlink} disabled={saving} className={slideOverCancelClass}>
          {t('waste.link.unlink')}
        </button>
      )}
      <button type="button" onClick={handleSave} disabled={saving || !selectedKey} className={slideOverPrimaryClass}>
        {saving ? t('waste.link.saving') : t('waste.link.confirm')}
      </button>
    </>
  );

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('waste.link.singleTitle')}
      description={t('waste.link.singleDescriptionIngredient')}
      footer={footer}
      closeLabel={t('waste.link.cancel') as string}
    >
      {entry && (
        <>
          {/* What is being linked */}
          <div className={cn('mb-5 p-3 rounded-xl border', borderPrimary)}>
            <p className={cn(smallTextSize, semiBoldText, primaryText)}>{entry.itemName}</p>
            <p className={cn('mt-1 text-[11px]', tertiaryText)}>
              {[
                formatWasteQuantity(entry.quantity, entry.unit),
                entry.reason ? t(WASTE_REASON_LABELS[entry.reason] || entry.reason) : null,
                t('waste.typeIngredient'),
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className={cn(smallTextSize, 'text-red-700 dark:text-red-300')}>{error}</p>
            </div>
          )}

          <TabbedSections
            options={[
              { value: 'suggested', label: t('waste.link.tabSuggested') },
              { value: 'browse', label: t('waste.link.tabProducts') },
            ]}
            value={tab}
            onChange={(v) => setTab(v as 'suggested' | 'browse')}
            isTimeOptionSelection={false}
            fullWidth
            className="mb-4"
          />

          {/* Supplier + product-category filters. Same layout as ReLinkInventoryProductModal. */}
          {tab === 'browse' && (
            <div className="mb-4 flex gap-2 flex-wrap">
              <LabeledFilterButton
                label={t('waste.link.filterSupplier')}
                selected={selectedSuppliers}
                onChange={setSelectedSuppliers}
                options={supplierOptions}
                valueType="platform"
              />
              <LabeledFilterButton
                label={t('waste.link.filterCategory')}
                selected={selectedCategories}
                onChange={setSelectedCategories}
                options={categoryOptions}
                valueType="category"
              />
            </div>
          )}

          {tab === 'browse' && (
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('waste.link.searchPlaceholder') as string}
                className={cn(
                  'w-full pl-9 pr-3 py-2 rounded-xl border text-sm',
                  borderPrimary,
                  'bg-white dark:bg-zinc-900',
                  primaryText,
                  'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                )}
              />
            </div>
          )}

          {/* Results — bordered container with a capped, scrollable list (inventory idiom). */}
          <div className={cn('p-3 rounded-lg border flex flex-col', borderPrimary)}>
            {isEmpty ? (
              <p className={cn(smallTextSize, secondaryText, 'text-center py-6')}>
                {tab === 'suggested' ? t('waste.link.noSuggestions') : t('waste.link.noCandidates')}
              </p>
            ) : (
              <>
                <p className={cn('mb-3 flex items-center gap-1 flex-shrink-0', smallTextSize, semiBoldText, primaryText)}>
                  {tab === 'suggested' ? t('waste.link.tabSuggested') : t('waste.link.tabProducts')}
                  <span>({visibleProducts.length})</span>
                </p>
                <div className="max-h-[55vh] overflow-y-auto space-y-2 custom-scrollbar">
                  {visibleProducts.map((p) => renderOption(p.key, p.productName, productSubtitle(p)))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </InventorySlideOver>
  );
}
